import { format, isValid, parse, parseISO, startOfDay } from "date-fns";
import type { ExpenseCategory } from "../../types/report";

export const MONEY_TOLERANCE = 0.01;

export function normalizeText(value: unknown): string {
  return String(value ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/[’‘]/g, "'")
    .replace(/[“”]/g, '"')
    .trim()
    .toLowerCase()
    .replace(/[._]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function parseMoney(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  const raw = String(value).trim();
  if (!raw || /moved[\s-]*out/i.test(raw)) return null;
  const negativeByParentheses = raw.startsWith("(") && raw.endsWith(")");
  const cleaned = raw.replace(/[$,\s]/g, "").replace(/[()]/g, "");
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed)) return null;
  return negativeByParentheses ? -parsed : parsed;
}

export interface CountResult {
  value: number | null;
  rounded: boolean;
  valid: boolean;
}

export function parseCount(value: unknown): CountResult {
  const parsed = parseMoney(value);
  if (parsed === null || parsed < 0) {
    return {
      value: parsed === null ? null : parsed,
      rounded: false,
      valid: false,
    };
  }
  return {
    value: Number.isInteger(parsed) ? parsed : Math.round(parsed),
    rounded: !Number.isInteger(parsed),
    valid: Number.isInteger(parsed),
  };
}

const DATE_FORMATS = [
  "d-MMM-yy",
  "d-MMM-yyyy",
  "M/d/yyyy",
  "M/d/yy",
  "MMMM d, yyyy",
  "MMM d, yyyy",
];

export function parseDateValue(value: unknown): string | null {
  let date: Date | null = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "number" && value > 0) {
    const utc = new Date(Date.UTC(1899, 11, 30) + value * 86_400_000);
    date = new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
  } else if (typeof value === "string") {
    const raw = value.trim();
    const iso = parseISO(raw);
    if (isValid(iso) && /^\d{4}-\d{2}-\d{2}/.test(raw)) date = iso;
    if (!date) {
      for (const pattern of DATE_FORMATS) {
        const candidate = parse(raw, pattern, new Date(2000, 0, 1));
        if (isValid(candidate)) {
          date = candidate;
          break;
        }
      }
    }
  }
  return date && isValid(date) ? format(startOfDay(date), "yyyy-MM-dd") : null;
}

export function normalizeCompanyName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\bdr\.?\b/g, "")
    .replace(/\bdmd\b/g, "")
    .replace(/\binc\.?\b/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .replace(/\s+/g, " ");
}

export const EXPENSE_ALIASES: Record<string, ExpenseCategory> = {
  "management fee": "management_fee",
  "total payroll": "payroll",
  payroll: "payroll",
  tax: "tax",
  taxes: "tax",
  insurance: "insurance",
  workerscomp: "workers_compensation",
  "workers comp": "workers_compensation",
  "workers compensation": "workers_compensation",
  "credit card payper roll": "credit_card_fee",
  "credit card payroll": "credit_card_fee",
  "credit card subscription": "subscription",
  "insurance workers copm": "insurance",
  "ticket monthly and daily": "ticket_cost",
  "ticket monthly daily": "ticket_cost",
  supplies: "supplies",
};

export function classifyExpense(label: string): ExpenseCategory {
  const normalized = normalizeText(label)
    .replace(/\//g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return EXPENSE_ALIASES[normalized] ?? "other";
}

function levenshtein(left: string, right: string): number {
  const matrix = Array.from({ length: left.length + 1 }, () =>
    Array<number>(right.length + 1).fill(0),
  );
  for (let row = 0; row <= left.length; row += 1) matrix[row]![0] = row;
  for (let col = 0; col <= right.length; col += 1) matrix[0]![col] = col;
  for (let row = 1; row <= left.length; row += 1) {
    for (let col = 1; col <= right.length; col += 1) {
      matrix[row]![col] =
        left[row - 1] === right[col - 1]
          ? matrix[row - 1]![col - 1]!
          : 1 +
            Math.min(
              matrix[row - 1]![col]!,
              matrix[row]![col - 1]!,
              matrix[row - 1]![col - 1]!,
            );
    }
  }
  return matrix[left.length]![right.length]!;
}

export function stringSimilarity(left: string, right: string): number {
  if (left === right) return 1;
  const longest = Math.max(left.length, right.length);
  return longest === 0 ? 1 : 1 - levenshtein(left, right) / longest;
}
